import type * as React from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useQueryParamString } from 'react-use-query-param-string';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { rgba } from 'polished';
import styled from 'styled-components';

import {
  cleanURL,
  DOCS_LINKS,
  getUserParams,
  storeUserParams
} from '../../utils';
import { rotate } from '../../utils/animations';
import { Button, Heading, Spinner, Text } from '../design-system';

import { LinkWithQuery } from './LinkWithQuery';

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 40px;
  border: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};
  background: ${({ theme }) => rgba(theme.highlightColor, 0.05)};
  padding: 40px;
  position: relative;
`;

const Form = styled.form`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: flex-end;
  gap: 20px;

  &:has(.def-input-group:first-of-type:last-of-type) {
    grid-template-columns: 1fr;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.MOBILE}) {
    max-width: 100%;
    grid-template-columns: 1fr;
  }

  label {
    color: ${({ theme }) => theme.fontColor.DEFAULT};
    text-transform: uppercase;
    font-family: ${({ theme }) => theme.fontFamily.MONO};
    font-size: ${({ theme }) => theme.fontSize.SMALL};
    font-style: normal;
    font-weight: 700;
    line-height: 22px;
    text-align: justify;
  }

  .def-form-checkbox-options-container {
    display: flex;
    flex-direction: column;

    label {
      display: flex;
      align-items: center;
      white-space: nowrap;
    }
  }

  input[type='checkbox'] {
    margin-right: 8px;
  }

  input {
    font-family: ${({ theme }) => theme.fontFamily.SANS};
    padding: 10px 15px;
    font-size: ${({ theme }) => theme.fontSize.DEFAULT};
    border: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};
    background: ${({ theme }) => theme.backgroundColor};
    color: ${({ theme }) => theme.fontColor.DEFAULT};
  }

  select {
    font-family: ${({ theme }) => theme.fontFamily.SANS};
    padding: 10px 15px;
    font-size: ${({ theme }) => theme.fontSize.DEFAULT};
    border: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};
    background: ${({ theme }) => theme.backgroundColor};
    color: ${({ theme }) => theme.fontColor.DEFAULT};
    outline: none;
    appearance: none;
    background-image: url("data:image/svg+xml,<svg height='10px' width='10px' viewBox='0 0 16 16' fill='%23f5c0c0' xmlns='http://www.w3.org/2000/svg'><path d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/></svg>");
    background-repeat: no-repeat;
    background-position: calc(100% - 0.75rem) center !important;
  }

  input:focus-visible {
    outline: 1px solid ${({ theme }) => theme.borderColor.LOUD};
  }

  .def-input-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .def-form-submit {
    font-family: ${({ theme }) => theme.fontFamily.SANS};
    position: relative;
    flex: 0 0 auto;
    display: inline-table;
    height: 40px;
    outline: none;
    padding: 0px 20px;
    user-select: none;
    cursor: pointer;
    font-size: 14px;
    max-width: 100px;
    color: rgb(245, 192, 192);
    min-width: 0px;
    transition:
      background-color 0.25s cubic-bezier(0.25, 0.1, 0.25, 1) 0s,
      border-color 0.25s cubic-bezier(0.25, 0.1, 0.25, 1) 0s;
    border: 1px solid transparent;
    background:
      linear-gradient(to right, rgb(1, 1, 27), rgb(1, 1, 27)) padding-box
        padding-box,
      linear-gradient(45deg, rgb(245, 192, 192), rgb(173, 142, 182)) border-box
        border-box;
  }

  .def-form-submitting {
    opacity: 0.5;
    pointer-events: none;

    &::after {
      content: '';
      display: block;
      width: 30px;
      height: 30px;
      border-radius: 50%;
      border: 3px solid #ccc;
      border-top-color: #333;
      animation: ${rotate} 1s linear infinite;
    }
  }
  .def-work-email {
    position: relative;
  }

  .def-form-errors {
    @media (min-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
      position: absolute;
      right: 0;
    }
  }

  .def-form-error-message {
    font-size: ${({ theme }) => theme.fontSize.SMALL};
    color: white;
  }
`;

const Disclaimer = styled.p`
  font-size: ${({ theme }) => theme.fontSize.SMALL};
  line-height: ${({ theme }) => theme.lineHeight.SMALL};
`;

const Confirmation = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  justify-content: center;
  padding: 40px;
`;

const ConfirmationText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  > div {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    margin-bottom: 30px;
  }

  h3 {
    margin-bottom: 10px;
  }

  p {
    margin-bottom: 8px;
  }

  ul {
    margin-left: 8px;

    li p {
      margin-bottom: 4px;
    }
  }
`;

interface DefaultFormProps {
  autoFill?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  confirmationMessage?: any;
  enableFormPersistence?: boolean;
  formId: string;
  hardcodedConfirmationMessage?: {
    message: string;
    title: string;
  };
  mediaToOpen?: {
    type: string;
    url: string;
  };
  slug: string;
}

const DefaultForm: React.FC<DefaultFormProps> = ({
  formId,
  slug,
  mediaToOpen,
  confirmationMessage,
  hardcodedConfirmationMessage,
  enableFormPersistence,
  autoFill
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const lsName = cleanURL(`${slug}-form-submitted`);
  const [isBoothForm] = useQueryParamString('is-booth-form', '');
  const confimationText = confirmationMessage
    ? JSON.parse(confirmationMessage)
    : null;

  const isBoothFormRef = useRef(isBoothForm === 'true');
  useLayoutEffect(() => {
    isBoothFormRef.current = isBoothForm === 'true';
  }, [isBoothForm]);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://import-cdn.default.com/default-embed.js';
    script.async = true;

    // Preload form fields
    const preloadLink = document.createElement('link');
    preloadLink.rel = 'preload';
    preloadLink.as = 'fetch';
    preloadLink.href = `https://nucleus.default.com/embed/form/${formId}/200`;
    preloadLink.crossOrigin = 'anonymous';
    document.head.appendChild(preloadLink);

    script.addEventListener('load', () => {
      // The external script has finished loading
      // Set isLoading to false to remove the GIF
      setIsLoading(false);
    });

    // Append the script to the head of the document
    document.head.appendChild(script);

    // Define the default form configuration with the provided formId
    const observerCallback = (
      mutations: MutationRecord[],
      observer: MutationObserver
    ) => {
      mutations.forEach(mutation => {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'class'
        ) {
          const target = mutation.target as HTMLElement;
          if (target.classList.contains('def-form-submit-success')) {
            setHasAccess(true);

            if (isBoothFormRef.current) return;

            localStorage.setItem(lsName, 'true');

            observer.disconnect();
            if (mediaToOpen) {
              window.open(mediaToOpen.url, '_blank');
            }
          }
        }
      });
    };

    const observer = new MutationObserver(observerCallback);
    const config = { attributes: true, attributeFilter: ['class'] };

    const checkForForm = () => {
      const formElement = document.querySelector('.def-form-ready');
      if (formElement instanceof HTMLFormElement) {
        observer.observe(formElement, config);

        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        clearInterval(intervalId); // Stop checking once the form is found

        // store user email in local storage on submit
        formElement.addEventListener('submit', e => {
          if (e.currentTarget instanceof HTMLFormElement) {
            const node = e.currentTarget.querySelector('input[type="email"]');
            if (node instanceof HTMLInputElement) {
              const email = node.value;
              storeUserParams({ email });
            }
          }
        });

        // Autofill and submit form if this user has previously:
        // 1. Come with an `?email=<user@aol.com>`
        // 2. Previously filled out a form
        if (!autoFill) return;
        const userEmail = getUserParams()?.email;
        if (!userEmail) return;

        const emailInput = formElement?.querySelector('input[type="email"]');
        if (!(emailInput instanceof HTMLInputElement)) return;

        emailInput.value = userEmail;
        for (const input of formElement.querySelectorAll('input')) {
          if (!(input instanceof HTMLInputElement)) continue;
          input.removeAttribute('required');
        }
        formElement.requestSubmit();
      }
    };

    // Get the access parameter from the URL
    const urlParams = new URLSearchParams(window.location.search);

    // Initial check for local storage to determine access
    if (
      enableFormPersistence &&
      (localStorage.getItem(lsName) === 'true' ||
        urlParams.get('access') === 'true')
    ) {
      setHasAccess(true);
      return; // No need to set up observer or interval
    }

    // Perform an immediate check in case the form loads quickly
    const intervalId = setInterval(checkForForm, 1000);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__default__ = { form_id: formId, team_id: 200 };

    // Rudderstack identify
    if (typeof window !== 'undefined' && window.rudderanalytics) {
      document.addEventListener('default-form-onsubmit', event => {
        const responses = (event as CustomEvent).detail.responses;
        const formId = (event as CustomEvent).detail.formId;

        window.rudderanalytics.track('Marketing Form Submitted', {
          form_responses: responses,
          form_id: formId
        });
      });
    }

    // Clean up the script element when the component unmounts
    return () => {
      document.head.removeChild(script);
      clearInterval(intervalId);
      observer.disconnect();
    };
  }, [formId, lsName, mediaToOpen, autoFill, enableFormPersistence]);

  return (
    <Wrapper>
      {!hasAccess ? (
        <>
          <Form id={`def-form-${formId}`}>
            {isLoading ? <Spinner /> : null}
          </Form>
          <Disclaimer>
            By submitting this form you agree to the processing of your personal
            data as described in our{' '}
            <LinkWithQuery isExternal={true} to={DOCS_LINKS.TermsAndConditions}>
              Terms
            </LinkWithQuery>{' '}
            and our{' '}
            <LinkWithQuery isExternal={true} to={DOCS_LINKS.PrivacyPolicy}>
              Privacy policy
            </LinkWithQuery>
            .
          </Disclaimer>
        </>
      ) : (
        <Confirmation className="default-form-confirmation-message">
          <ConfirmationText>
            {mediaToOpen && (
              <>
                <div>
                  <Heading renderAs="h3">Access the {mediaToOpen.type}</Heading>
                  <Text size="large">Click below to view this content.</Text>
                </div>
                <Button link={mediaToOpen.url} stylez="loud" text="Open" />
              </>
            )}
            {!mediaToOpen && confimationText && (
              <>{documentToReactComponents(confimationText)}</>
            )}
            {!mediaToOpen && !confimationText && (
              <>
                <Heading renderAs="h3">
                  {hardcodedConfirmationMessage
                    ? hardcodedConfirmationMessage.title
                    : 'Thank you'}
                </Heading>
                <Text size="default">
                  {hardcodedConfirmationMessage
                    ? hardcodedConfirmationMessage.message
                    : 'This form has been submitted.'}
                </Text>
              </>
            )}
          </ConfirmationText>
        </Confirmation>
      )}
    </Wrapper>
  );
};

export default DefaultForm;
